import {
  Body,
  Box,
  Label,
  Radio,
  Stack,
  styledPolymorphic,
} from '@qred/components-library';
import React from 'react';
import { Grid } from '../Layout/Grid';

export type RadioGroupLayout = 'grid' | 'stacked';

interface RadioGroupOption<T extends string | number> {
  label: string;
  value: T;
  description?: React.ReactNode;
  rightSection?: React.ReactNode;
}

interface Props<T extends string | number = string> {
  options: RadioGroupOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** 'grid' shows the options as separate boxes, 'stacked' as one column of joined rows. */
  layout?: RadioGroupLayout;
}

export const RadioGroup = <T extends string | number>(props: Props<T>) => {
  const { value, options, onChange, layout = 'grid' } = props;
  const isStacked = layout === 'stacked';
  const OptionsContainer = isStacked ? StackedOptions : Grid;

  const onClick = (targetValue: T) => {
    if (targetValue !== value) {
      onChange(targetValue);
    }
  };

  return (
    <Radio.Group>
      <OptionsContainer>
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <RadioWrap
              key={option.value}
              onClick={() => onClick(option.value)}
              $isSelected={isSelected}
              $isStacked={isStacked}
            >
              <Radio
                checked={isSelected}
                label={
                  <Stack spacing={'xs'}>
                    <Label size="lg">{option.label}</Label>
                    {option.description && (
                      <Body size="md" color="Text/Secondary">
                        {option.description}
                      </Body>
                    )}
                  </Stack>
                }
                value={option.value}
                size={'sm'}
              />
              {option.rightSection && (
                <Box ml={'auto'}>{option.rightSection}</Box>
              )}
            </RadioWrap>
          );
        })}
      </OptionsContainer>
    </Radio.Group>
  );
};

const StackedOptions = styledPolymorphic<'div', {}>()((styled) =>
  styled(() => ({
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%',
  }))
);

const RADIUS = '4px';

export const RadioWrap = styledPolymorphic<
  'div',
  { $isSelected?: boolean; $isStacked?: boolean }
>()((styled) =>
  styled(({ $isSelected, $isStacked, theme }) => ({
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '13px 17px',
    gap: '12px',
    borderRadius: $isStacked ? 0 : RADIUS,
    transition: 'all 0.2s ease',
    border: `1px solid ${
      $isSelected
        ? theme.fn.themeColor('Border/Active')
        : theme.fn.themeColor('Border/Default')
    }`,
    '&:hover': {
      backgroundColor: $isSelected
        ? theme.fn.themeColor('transparent')
        : theme.fn.themeColor('Container/Option_1_(Greige_I)'),
    },
    // Joined rows share one border, and the selected row is drawn on top so its whole border shows
    ...($isStacked && {
      position: 'relative' as const,
      zIndex: $isSelected ? 1 : 0,
      '& + &': { marginTop: '-1px' },
      '&:first-of-type': {
        borderTopLeftRadius: RADIUS,
        borderTopRightRadius: RADIUS,
      },
      '&:last-of-type': {
        borderBottomLeftRadius: RADIUS,
        borderBottomRightRadius: RADIUS,
      },
    }),
  }))
);
